import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { useEffect, lazy, Suspense } from 'react'
import { LoginPage } from './pages/Login'
import { OAuthCallback } from './pages/OAuthCallback'
import { LoadingScreen } from './components/LoadingScreen'
import { NotFound } from './components/NotFound'
import { CookieConsentBanner } from './components/common/CookieConsentBanner'

// Main pages
const AbyssPage = lazy(() => import('./pages/Abyss'))

// Test pages
import { CorruptionTest } from './pages/CorruptionTest'

import { setPainCSSVariables } from './utils/painCSSVariables'
import { useLanguageStore } from '@darkchat/sdk'
import './styles/pain-colors.css'
import './styles/dynamic-colors.css'
import './styles/suffering-effects.css'

function App() {
  const { initializeLanguage } = useLanguageStore()

  useEffect(() => {
    // Initialize language from URL/localStorage/browser
    initializeLanguage()

    // Set dark theme
    setPainCSSVariables()
    document.documentElement.classList.add('dark')
  }, [])

  return (
    <BrowserRouter>
      <CookieConsentBanner />
      <Routes>
        {/* Root path */}
        <Route path="/" element={<LoginPage />} />

        {/* Japanese language routes */}
        <Route path="/ja" element={<LoginPage />} />
        <Route path="/ja/oauth/callback" element={<OAuthCallback />} />
        <Route path="/ja/abyss" element={
          <Suspense fallback={<LoadingScreen message="深淵への落下中..." />}>
            <AbyssPage />
          </Suspense>
        } />
        <Route path="/ja/abyss/:roomId" element={
          <Suspense fallback={<LoadingScreen message="部屋を準備中..." />}>
            <AbyssPage />
          </Suspense>
        } />
        <Route path="/ja/loadingscreen" element={<LoadingScreen message="ロード中..."/>} />
        <Route path="/ja/soul" element={<LoadingScreen message="魂の記録を読み込み中..." />} />
        <Route path="/ja/codex" element={<LoadingScreen message="法典を開いています..." />} />
        <Route path="/ja/status" element={<LoadingScreen message="深淵の状態を確認中..." />} />
        <Route path="/ja/about" element={<LoadingScreen message="教典を読み込み中..." />} />
        <Route path="/ja/archives" element={<LoadingScreen message="記録庫を探索中..." />} />
        <Route path="/ja/keywords/:keyword?" element={<LoadingScreen message="禁忌の知識を解読中..." />} />
        <Route path="/ja/test/corruption" element={<CorruptionTest />} />

        {/* English language routes */}
        <Route path="/en" element={<LoginPage />} />
        <Route path="/en/oauth/callback" element={<OAuthCallback />} />
        <Route path="/en/abyss" element={
          <Suspense fallback={<LoadingScreen message="Falling into the abyss..." />}>
            <AbyssPage />
          </Suspense>
        } />
        <Route path="/en/abyss/:roomId" element={
          <Suspense fallback={<LoadingScreen message="Preparing room..." />}>
            <AbyssPage />
          </Suspense>
        } />
        <Route path="/en/loadingscreen" element={<LoadingScreen message="Loading..."/>} />
        <Route path="/en/soul" element={<LoadingScreen message="Loading soul records..." />} />
        <Route path="/en/codex" element={<LoadingScreen message="Opening codex..." />} />
        <Route path="/en/status" element={<LoadingScreen message="Checking abyss status..." />} />
        <Route path="/en/about" element={<LoadingScreen message="Loading scripture..." />} />
        <Route path="/en/archives" element={<LoadingScreen message="Exploring archives..." />} />
        <Route path="/en/keywords/:keyword?" element={<LoadingScreen message="Decoding forbidden knowledge..." />} />
        <Route path="/en/test/corruption" element={<CorruptionTest />} />

        {/* Default routes without language prefix (for backward compatibility) */}
        <Route path="/abyss" element={
          <Suspense fallback={<LoadingScreen message="深淵への落下中..." />}>
            <AbyssPage />
          </Suspense>
        } />
        <Route path="/abyss/:roomId" element={
          <Suspense fallback={<LoadingScreen message="部屋を準備中..." />}>
            <AbyssPage />
          </Suspense>
        } />
        <Route path="/loadingscreen" element={<LoadingScreen message="ロード中..."/>} />
        <Route path="/soul" element={<LoadingScreen message="魂の記録を読み込み中..." />} />
        <Route path="/codex" element={<LoadingScreen message="法典を開いています..." />} />
        <Route path="/status" element={<LoadingScreen message="深淵の状態を確認中..." />} />
        <Route path="/about" element={<LoadingScreen message="教典を読み込み中..." />} />
        <Route path="/archives" element={<LoadingScreen message="記録庫を探索中..." />} />
        <Route path="/keywords/:keyword?" element={<LoadingScreen message="禁忌の知識を解読中..." />} />

        {/* 404 handling with language-aware redirects */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
